/**
 * Lectures Table - Frontend
 * @description Tabla 3: Shows lectures for a specific operation
 * Refactored to follow DeviceOperationsTable pattern with organized cell components
 * Includes export functionality
 */

import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';

// Types
import { LecturesTableData, DashboardOperationTableActions } from '@/types/operational-ui.types';

// UI Components from the main system
import { TableHeader } from '@/components/shared/tables/TableHeader/TableHeader';
import { TableRow } from '@/components/shared/tables/TableRow/TableRow';
// import { TableButton } from '@/components/ui/TableButton';
import { FilterDialog } from '@/components/shared/FilterDialog';

// Hooks from the main system
import { useTableFilters } from '@/hooks/useTableFilters';

// Configuration
import { getLecturesTableColumns } from '@/constants/tables/lecturesTable.config';

// Utils

interface LecturesTableProps {
  lectures: LecturesTableData[];
  loading?: boolean;
  actions?: DashboardOperationTableActions;
  showSequence?: boolean; // New prop to show sequence column
  showOperationId?: boolean; // New prop to show operation id column
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

export const LecturesTable: React.FC<LecturesTableProps> = ({
  lectures,
  loading = false,
  showSequence = false,
  showOperationId = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  // TODO: Mostrar ID de operacion opcional
  // showAdvancedExport = true,
}) => {
  const { t } = useTranslation();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>(() =>
    showSequence ? [{ id: 'sequence', desc: true }] : []
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(500);
  const rowHeight = 42;
  const overscan = 8;

  // Get column configuration
  const columnConfigs = useMemo(
    () => getLecturesTableColumns(t, showSequence, showOperationId),
    [t, showSequence, showOperationId]
  );

  const {
    filterState,
    columnFilters,
    openFilter,
    closeFilter,
    setSearch,
    toggleOption,
    clearFilter,
    applyFilter,
    getFilteredOptions,
  } = useTableFilters(lectures, columnConfigs);

  const columnHelper = createColumnHelper<LecturesTableData>();

  // Build columns from config
  const columns: ColumnDef<LecturesTableData>[] = useMemo(
    () => [
      ...columnConfigs.map((config) =>
        columnHelper.accessor(
          config.accessor as any, // eslint-disable-line @typescript-eslint/no-explicit-any
          {
            id: config.id,
            header: ({ column }) => {
              const filterValue = column.getFilterValue() as string[] | undefined;
              const isFiltered = Array.isArray(filterValue) && filterValue.length > 0;
              return (
                <TableHeader
                  header={column}
                  columnConfig={config}
                  onFilterClick={(columnId) => openFilter(columnId, columnFilters)}
                  isFiltered={isFiltered}
                  justifyHeader={true}
                />
              );
            },
            cell:
              config.cell ||
              (({ getValue }) => {
                const value = getValue();
                return <span className="text-gray-900">{value?.toString() || '-'}</span>;
              }),
            enableSorting: config.enableSorting,
            enableColumnFilter: config.enableFilter,
            filterFn:
              config.filterType === 'select'
                ? (row, _columnId, filterValue) => {
                    if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
                    const value = row.getValue(_columnId);
                    return filterValue.includes(value?.toString() || '');
                  }
                : undefined,
          } as ColumnDef<LecturesTableData>
        )
      ),
    ],
    [columnConfigs, columnHelper, openFilter, columnFilters]
  );

  const table = useReactTable({
    data: lectures,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleRowClick = (lectureId: string | undefined) => {
    if (!lectureId) return;
    setSelectedRowId((current) => (current === lectureId ? null : lectureId));
  };

  const tableRows = table.getRowModel().rows;
  const totalRows = tableRows.length;
  const visibleCount = Math.max(Math.ceil(containerHeight / rowHeight), 1) + overscan * 2;
  const startIndex = Math.max(Math.floor(scrollTop / rowHeight) - overscan, 0);
  const endIndex = Math.min(startIndex + visibleCount, totalRows);
  const virtualRows = tableRows.slice(startIndex, endIndex);
  const topPadding = startIndex * rowHeight;
  const bottomPadding = Math.max((totalRows - endIndex) * rowHeight, 0);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      setScrollTop(target.scrollTop);
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 200;
      if (nearBottom && hasMore && !isLoadingMore && onLoadMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });
    resizeObserver.observe(container);
    setContainerHeight(container.clientHeight);
    return () => resizeObserver.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="bg-brand-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-lg text-gray-600">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-white rounded-lg shadow-lg">
      {/* Filter Dialogs */}
      {columnConfigs
        .filter((config) => config.enableFilter)
        .map((config) => {
          const filterTitleKey = `pages.lectures.filters.${config.id}`;
          return (
            <FilterDialog
              key={config.id}
              isOpen={!!filterState[config.id]?.isOpen}
              onClose={() => closeFilter(config.id)}
              title={t(filterTitleKey)}
              searchValue={filterState[config.id]?.search || ''}
              onSearchChange={(value) => setSearch(config.id, value)}
              options={getFilteredOptions(config.id)}
              selectedOptions={filterState[config.id]?.selected || []}
              onOptionToggle={(option) => toggleOption(config.id, option)}
              onClear={() => clearFilter(config.id)}
              onApply={() => applyFilter(config.id)}
            />
          );
        })}

      {/* Table */}
      <div className="overflow-x-auto">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 300px)' }}
        >
          <table className="w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-brand-gray-dark">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-center text-xs font-medium text-brand-white sentence-case tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {topPadding > 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ height: `${topPadding}px` }} />
                </tr>
              )}
              {virtualRows.map((row, index) => (
                <TableRow
                  key={row.id}
                  row={row}
                  index={startIndex + index}
                  selectedRowId={selectedRowId}
                  onRowClick={() => handleRowClick(row.original.id || row.original.lectureId)}
                  className="px-1 py-1 whitespace-nowrap text-sm text-brand-gray-dark"
                />
              ))}
              {bottomPadding > 0 && (
                <tr>
                  <td colSpan={columns.length} style={{ height: `${bottomPadding}px` }} />
                </tr>
              )}
            </tbody>
          </table>
          {isLoadingMore && (
            <div className="py-3 text-center text-sm text-gray-500 border-t border-gray-200 flex items-center justify-center gap-2">
              <span className="animate-spin inline-block w-4 h-4 border-2 border-gray-300 border-t-brand-orange rounded-full"></span>
              <span>{t('common.loading')}...</span>
            </div>
          )}
        </div>
      </div>

      {/* Empty state */}
      {lectures.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('pages.lectures.empty.title')}
          </h3>
          <p className="text-gray-500">{t('pages.lectures.empty.description')}</p>
        </div>
      )}

    </div>
  );
};
